import { Injectable } from '@angular/core';
import { FormField } from '../../interfaces/form-field.interface';
import { MultipartFile } from '../../components/controls/multipart-files/multipart-file';
import { FormVariable, FormVariables } from '../../interfaces/form-variables.interface';
import { isDictionaryItem } from '../../interfaces/dictionary-item.interface';

@Injectable()
export class FormDataService {
  public mapFormRawDataToFormVariables(formData: { [key: string]: object }, form: FormField[]): FormVariables {
    const result: FormVariables = { variables: null, multipartFiles: [] };
    result.variables = this.handleFormRawData(formData, form, result.multipartFiles);
    return result;
  }

  private handleFormRawData(formData: { [key: string]: object }, form: FormField[], resultFiles: MultipartFile[]): FormVariable[] {
    const variables: FormVariable[] = [];

    for (const key in formData) {
      const field = this.getFormField(key, form);
      const value = formData[key];

      if (field.type === 'multipartFiles') {
        variables.push(this.handleMultipartFiles(key, value as MultipartFile[], resultFiles));
      } else if (field.type === 'list') {
        variables.push(this.handleListField(field, value as any[], resultFiles));
      } else {
        variables.push({ name: key, value });
      }
    }

    return variables;
  }

  private getFormField(key: string, form: FormField[]): FormField {
    const field = form.find((field) => field.id === key);
    if (!field) {
      throw new Error(`Cannot find field with id: '${key}'`);
    }
    return field;
  }

  private handleListField(field: FormField, arrayValue: any[], resultMultipartFiles: MultipartFile[]): FormVariable {
    if (arrayValue == null) {
      return { name: field.id, value: null };
    }

    const subFormList = [];
    for (const element of arrayValue) {
      subFormList.push(this.handleFormRawData(element as {}, field.subForm.definition, resultMultipartFiles));
    }
    return { name: field.id, value: subFormList };
  }

  private handleMultipartFiles(variableName: string, multipartFiles: MultipartFile[], resultMultipartFiles: MultipartFile[]): FormVariable {
    if (multipartFiles == null) {
      return { name: variableName, value: null };
    }

    const fileNameArray: string[] = [];

    multipartFiles.forEach((part) => {
      if (MultipartFile.isMultipartFile(part)) {
        resultMultipartFiles.push(part);
        fileNameArray.push(part.fileNameWithPath);
      } else {
        throw new Error('The array contains a type other than File');
      }
    });

    return { name: variableName, value: fileNameArray };
  }

  public mapVariablesToFormData(formVariables: FormVariables): FormData {
    const formData = new FormData();
    formVariables.multipartFiles.forEach((multipartFile) => formData.append('multipartFiles', multipartFile.file, multipartFile.fileNameWithPath));
    formData.append('variables', this.getVariablesBlob(formVariables.variables));
    return formData;
  }

  private getVariablesBlob(variables: Array<FormVariable>): Blob {
    const variablesObject = this.mapVariablesToVariablesObject(variables);
    return new Blob([JSON.stringify(variablesObject, null, 2)], { type: 'application/json' });
  }

  public mapVariablesToVariablesObject(variables: Array<FormVariable>): object {
    const variablesObject = {};

    if (variables && Array.isArray(variables)) {
      variables.forEach((variable) => {
        if (this.isArrayOfVariables(variable.value)) {
          variablesObject[variable.name] = this.mapArrayOfVariablesToVariablesObject(variable.value);
        } else {
          variablesObject[variable.name] = variable.value;
        }
      });
    }

    return variablesObject;
  }

  private isArrayOfVariables(value: any): boolean {
    return Array.isArray(value) && value.length && typeof value[0] === 'object' && !isDictionaryItem(value[0]);
  }

  private mapArrayOfVariablesToVariablesObject(arrayVariable: any[]): object[] {
    const variableArray = [];
    for (const element of arrayVariable) {
      variableArray.push(this.mapVariablesToVariablesObject(element));
    }
    return variableArray;
  }
}



// WEBPACK FOOTER //
// ./src/submodules/sellions_forms/projects/forms/src/lib/services/form-data/form-data.service.ts